<template>
  <div class="map-container">
    <!-- Diagnostics Panel (alleen zichtbaar in admin mode) -->
    <DiagnosticsPanel 
      v-if="showDiagnosticsPanel && diagnosticsPanelData"
      :data="diagnosticsPanelData"
    />
    
    <!-- Springt niet alleen naar de lijst maar klapt hem ook uit en zet de focus
         erin. Zonder dat kost het drie handelingen: activeren, nog een keer Tab
         (een <section> kan zelf geen focus krijgen), en dan pas de knop "Tonen".
         De href blijft staan als terugval zonder JavaScript. -->
    <a class="skip-link" href="#projectenlijst" @click.prevent="gaNaarLijst">Toon de lijst met projecten</a>
    <h1 class="visually-hidden">{{ mapConfig._name || "Projectenkaart" }}</h1>

    <nav class="sidebar" ref="filterSidebar" :class="{ 'sidebar-open': isPanelOpen }"
         aria-label="Filters en zoeken">
      <button
        type="button"
        class="sidebar-toggle"
        @click="isPanelOpen = !isPanelOpen"
        :aria-expanded="isPanelOpen"
        aria-controls="filterpaneel"
        :aria-label="isPanelOpen ? 'Filters verbergen' : 'Filters tonen'"
      >
        <i
          class="fas"
          :class="isPanelOpen ? 'fa-chevron-left' : 'fa-chevron-right'"
          aria-hidden="true"
        ></i>
      </button>

      <!-- inert wanneer dicht: het paneel staat op left:-300px maar de inhoud
           blijft anders gewoon focusbaar, dus dan tabt een gebruiker door
           filters die buiten beeld staan. -->
      <div class="filter-panel p-3" id="filterpaneel" :inert="!isPanelOpen || null">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h2 class="h5 mb-0">
            {{ mapConfig.filterPanel?.title || "Filters" }}
          </h2>
          <div class="d-flex align-items-center gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="resetFilters"
            >
              <i class="fas fa-undo" aria-hidden="true"></i> Reset
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="isPanelOpen = false"
              title="Filterpaneel sluiten"
              aria-label="Filterpaneel sluiten"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <!-- Zoekveld toevoegen als de zoekfunctie is ingeschakeld -->
        <div v-if="mapConfig.searchEnabled" class="mb-3">
          <label class="visually-hidden" for="kaart-zoeken">Zoeken in projecten</label>
          <input
            id="kaart-zoeken"
            type="search"
            class="form-control"
            placeholder="Zoeken..."
            v-model="searchQuery"
            @input="updateMarkers"
          />
        </div>
        <!-- Dynamisch gegenereerde filters -->
        <div
          v-for="filter in mapConfig.filters"
          :key="filter.field"
          class="mb-3"
        >
          <!-- De filtertitel zit ín de uitklapknop. Zo heeft die knop een naam
               (het was een icoon zonder tekst) en verdwijnt het losse <label>
               dat nergens aan gekoppeld was maar wel een klikhandler droeg. -->
          <div class="d-flex align-items-center">
            <button
              class="btn btn-link p-0 filter-toggle"
              type="button"
              :id="'filter-toggle-' + filter.field"
              :aria-expanded="!isCollapsed(filter.field)"
              :aria-controls="filter.field + '-collapse'"
              @click="toggleCollapse(filter.field)"
            >
              <i
                :class="
                  isCollapsed(filter.field)
                    ? 'fas fa-caret-right'
                    : 'fas fa-caret-down'
                "
                aria-hidden="true"
              ></i>
              <span class="ms-2 form-label mb-0">{{ filter.label }}</span>
            </button>
          </div>
          <div
            :id="filter.field + '-collapse'"
            :class="isCollapsed(filter.field) ? 'collapse' : 'collapse show'"
          >
            <div
              v-if="filter.type === 'checkbox'"
              role="group"
              :aria-labelledby="'filter-toggle-' + filter.field"
            >
              <div
                v-for="(option, i) in filter.options"
                :key="option.value"
                class="form-check form-switch"
              >
                <input
                  :id="'f-' + filter.field + '-' + i"
                  class="form-check-input"
                  type="checkbox"
                  v-model="selectedOptions[filter.field]"
                  :value="option.value"
                />
                <label class="form-check-label" :for="'f-' + filter.field + '-' + i">{{ option.label }}</label>
              </div>
            </div>
            <div v-else-if="filter.type === 'range'">
              <div class="slider-container mb-3">
                <p class="form-label mb-1" aria-live="polite">
                  {{ sliderRanges[filter.field]?.[0] }} -
                  {{ sliderRanges[filter.field]?.[1] }} {{ filter.range?.unit }}
                </p>
                <div :ref="filter.field + 'Slider'"></div>
              </div>
            </div>
            <div v-else-if="filter.type === 'select'">
              <select
                class="form-select"
                v-model="selectedOptions[filter.field]"
                :aria-labelledby="'filter-toggle-' + filter.field"
              >
                <option value="">Niet geselecteerd</option>
                <option
                  v-for="option in filter.options"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div
              v-else-if="filter.type === 'radio'"
              role="group"
              :aria-labelledby="'filter-toggle-' + filter.field"
            >
              <div
                v-for="(option, i) in filter.options"
                :key="option.value"
                class="form-check"
              >
                <input
                  :id="'f-' + filter.field + '-' + i"
                  type="radio"
                  class="form-check-input"
                  :name="filter.field"
                  :value="option.value"
                  v-model="selectedOptions[filter.field]"
                />
                <label class="form-check-label" :for="'f-' + filter.field + '-' + i">{{ option.label }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
    <!-- Bedieningsuitleg vóór de kaart. Google zet één marker op tabindex=0 en
         de rest op -1: je tabt de kaart in en beweegt daarbinnen met de
         pijltjes. Dat is een correcte constructie, maar volstrekt onvindbaar
         zonder uitleg — een toetsenbordgebruiker denkt dat hij vastzit. -->
    <p class="visually-hidden" id="kaart-uitleg">
      De kaart bevat {{ zichtbareItems.length }} projecten. Ga met Tab de kaart in
      en beweeg daarna met de pijltjestoetsen tussen de projecten; Enter of spatie
      opent de details. Met Tab verlaat u de kaart weer. Liever een tekstversie?
      Gebruik bovenaan de link naar de lijst met projecten.
    </p>
    <div id="map" role="application"
         aria-label="Kaart met projecten"
         aria-describedby="kaart-uitleg"></div>

    <!-- Zichtbare tegenhanger van bovenstaande uitleg, alleen als de focus in de
         kaart staat. Voor een ziende toetsenbordgebruiker is verborgen tekst
         geen hulp; die ziet alleen dat Tab hem de kaart uit stuurt.
         aria-hidden omdat de schermlezer de tekst al via aria-describedby krijgt. -->
    <div class="kaart-hint" aria-hidden="true">
      Beweeg met de <kbd>pijltjestoetsen</kbd> tussen projecten ·
      <kbd>Enter</kbd> opent de details · <kbd>Tab</kbd> verlaat de kaart
    </div>

    <button
      type="button"
      class="map-fullscreen-toggle"
      @click="toggleFullscreen"
      :title="isFullscreen ? 'Volledig scherm verlaten' : 'Volledig scherm'"
      :aria-label="isFullscreen ? 'Volledig scherm verlaten' : 'Volledig scherm'"
    >
      <i class="fas" :class="isFullscreen ? 'fa-compress' : 'fa-expand'"></i>
    </button>

    <SidebarMarkerDisplay
      v-if="displayMode === 'sidebar'"
      ref="markerDisplay"
    />

    <!-- Tekstalternatief voor de kaart (WCAG 1.1.1). De kaartlaag zelf komt van
         Google; deze lijst is code die we bezitten en kunnen testen, en volgt
         exact dezelfde filters. -->
    <!-- Dicht is dit een kleine knop in de hoek, in dezelfde stijl als de
         kaartknoppen. Een altijd zichtbare balk over de volle breedte legt
         beslag op het scherm terwijl hij meestal niets doet. Bewust geen
         volledig verborgen paneel: het tekstalternatief moet ook vindbaar zijn
         voor wie niet met het toetsenbord navigeert. -->
    <button
      v-if="!lijstOpen"
      type="button"
      class="lijst-knop"
      @click="gaNaarLijst"
      aria-expanded="false"
      aria-controls="projectenlijst"
    >
      <i class="fas fa-list-ul" aria-hidden="true"></i>
      <span>Lijst</span>
      <span class="lijst-knop-telling">{{ zichtbareItems.length }}</span>
    </button>

    <section v-show="lijstOpen"
             id="projectenlijst" ref="projectenlijst" tabindex="-1"
             class="projectenlijst"
             aria-labelledby="projectenlijst-kop">
      <div class="lijst-kop">
        <h2 class="h6 mb-0" id="projectenlijst-kop">
          Projecten in lijstvorm
          <span class="badge bg-secondary ms-1">{{ zichtbareItems.length }}</span>
        </h2>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="sluitLijst"
          aria-expanded="true"
          aria-controls="projectenlijst"
        >
          Verbergen
        </button>
      </div>

      <div class="lijst-inhoud">
        <p class="visually-hidden" aria-live="polite">
          {{ zichtbareItems.length }} projecten voldoen aan de huidige filters.
        </p>

        <p v-if="!zichtbareItems.length" class="text-muted mb-0">
          Geen projecten gevonden met de huidige filters.
        </p>

        <div v-else class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <caption class="visually-hidden">
              Dezelfde projecten als op de kaart, met dezelfde filters toegepast.
            </caption>
            <thead>
              <tr>
                <th scope="col" v-for="kolom in lijstKolommen" :key="kolom.key">
                  {{ kolom.label }}
                </th>
                <th scope="col"><span class="visually-hidden">Details</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in zichtbareItems" :key="item.id || i">
                <td v-for="kolom in lijstKolommen" :key="kolom.key">
                  {{ lijstWaarde(item, kolom) }}
                </td>
                <td>
                  <button
                    type="button"
                    class="btn btn-sm btn-link p-0"
                    @click="toonDetailsUitLijst(item, $event)"
                  >
                    Details<span class="visually-hidden"> van {{ lijstWaarde(item, lijstKolommen[0]) }}</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
/* eslint-disable */
import { markRaw, createApp, h, ref, provide } from "vue";
import ServiceFactory from "@/services/ServiceFactory";
import noUiSlider from "nouislider";
import "nouislider/dist/nouislider.css";
import debounce from "lodash/debounce";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import MarkerTemplate from '@/components/marker-displays/MarkerTemplate.vue'
import SidebarMarkerDisplay from "@/components/marker-displays/SidebarMarkerDisplay.vue";
import DiagnosticsPanel from "@/components/DiagnosticsPanel.vue";
//import mapConfig from "../config/mapConfig.json";

export default {
  name: "MapView",
  components: {
    MarkerTemplate,
    SidebarMarkerDisplay,
    DiagnosticsPanel,
  },
  setup() {
    const mapConfig = ref({});  // Start met een lege config

    return {
      mapConfig  // Expose mapConfig zodat we het kunnen updaten in mounted
    };
  },
  data() {
    return {
      mapData: [],
      isPanelOpen: false,
      availableOptions: {},
      selectedOptions: {},
      sliderRanges: {},
      markers: [],
      markerItems: new Map(),
      spiderLegs: new Map(),
      map: null,
      markerClusterer: null,
      collapsedFilters: {},
      searchQuery: "",
      displayMode: ref('sidebar'),
      // Diagnostics voor admin panel
      diagnosticsData: null,
      isInIframe: false,
      parentOrigin: null,
      showDiagnosticsPanel: false,
      diagnosticsPanelData: null,
      isFullscreen: false,
      visibleMarkerCount: 0,
      // Stond hier niet in, terwijl updateMapData er wel aan toewijst. In Vue 3
      // is een ongedeclareerde property niet reactief, dus een computed of
      // template die hem gebruikt werkte nooit bij.
      properties: [],
      // Tekstalternatief voor de kaart. Wordt gevuld in updateMarkers, in
      // dezelfde lus die bepaalt welke markers zichtbaar zijn — zo kan de lijst
      // per constructie niet uit de pas lopen met wat er op de kaart staat.
      zichtbareItems: [],
      lijstOpen: false,
      pubFilterHiddenCount: 0,
      pubFilterActiveSnapshot: null,
      searchHiddenCount: 0,
      fieldFilterStats: [],
    };
  },
  async mounted() {
    // Detecteer of we in een iframe draaien
    this.isInIframe = window.self !== window.top;
    
    // Setup parent communication, maar stuur ready signaal pas na data load
    if (this.isInIframe) {
      window.addEventListener('message', this.handleParentMessage);
    }

    try {
      const mapService = ServiceFactory.getMapConfigService();
      const configId = this.$route.params.id;
      const config = await mapService.getMapConfig(configId);
      
      // Update de provided ref
      this.mapConfig = config;
      this.displayMode = config.displayMode || 'sidebar';  // sidebar|popup
      
      // Default clustering aan voor kaarten zonder clustering config
      if (!this.mapConfig.clustering) {
        this.mapConfig.clustering = { 
          enabled: true, 
          maxZoom: 15,
          colors: { small: "#3388ff", large: "#ff6b6b", border: "#ffffff", style: "solid" }
        };
      }
      // Zorg ervoor dat clustering.colors bestaat met alle properties
      if (!this.mapConfig.clustering.colors) {
        this.mapConfig.clustering.colors = { small: "#3388ff", large: "#ff6b6b", border: "#ffffff", style: "solid" };
      }
      if (!this.mapConfig.clustering.colors.border) {
        this.mapConfig.clustering.colors.border = "#ffffff";
      }
      if (!this.mapConfig.clustering.colors.style) {
        this.mapConfig.clustering.colors.style = "solid";
      }

      this.debouncedUpdateMarkers = debounce(this.updateMarkers, 300);
      const isMobile = window.innerWidth <= 768;
      this.isPanelOpen = isMobile
        ? this.mapConfig.filterPanel?.defaultOpen?.mobile ?? false
        : this.mapConfig.filterPanel?.defaultOpen?.desktop ?? true;

      // Initialiseer de beschikbare en geselecteerde opties
      this.mapConfig.filters.forEach((filter) => {
        if (filter.type === "checkbox") {
          this.availableOptions[filter.field] = filter.options.map(
            (opt) => opt.value
          );
          this.selectedOptions[filter.field] = filter.defaultSelected
            ? filter.options.map((opt) => opt.value)
            : [];
        }
        if (filter.type === "select" || filter.type === "radio") {
          // Standaard 'niet geselecteerd' (lege string), tenzij admin een defaultValue ingesteld heeft
          this.selectedOptions[filter.field] = filter.defaultValue ?? "";
        }
        // Dynamisch een watcher toevoegen voor elke checkbox filter
        this.$watch(
          () => this.selectedOptions[filter.field],
          () => {
            this.updateMarkers();
          },
          { deep: true }
        );
      });

      const dataService = ServiceFactory.getDataService();
      const mapDataResponse = await dataService.getMapData(this.mapConfig.id);

      // Nieuwe response-shape: { items: [...], _diagnostics: {...} }
      // Oudere backends stuurden gewoon een array; ondersteun beide.
      let items;
      if (Array.isArray(mapDataResponse)) {
        items = mapDataResponse;
        if (mapDataResponse._diagnostics) {
          this.diagnosticsData = mapDataResponse._diagnostics;
        }
      } else if (mapDataResponse && typeof mapDataResponse === 'object') {
        items = Array.isArray(mapDataResponse.items) ? mapDataResponse.items : [];
        if (mapDataResponse._diagnostics) {
          this.diagnosticsData = mapDataResponse._diagnostics;
        }
      } else {
        items = [];
      }

      this.mapData = items;
      this.properties = this.mapData;

      // Nu data geladen is, stuur ready signaal naar parent
      if (this.isInIframe) {
        this.sendToParent({ type: 'mapview-ready' });
      }

      if (window.google) {
        this.initMap();
      } else {
        window.addEventListener("load", () => {
          this.initMap();
        });
      }

      this.updateMarkerColors();
    } catch (error) {
      console.error("Error loading mapConfig:", error);
    }

    try {
      this.mapConfig.filters.forEach((filter) => {
        if (filter.type === "range") {
          const sliderElement = this.$refs[filter.field + "Slider"]?.[0];
          if (sliderElement) {
            noUiSlider.create(sliderElement, {
              start: [filter.range.min, filter.range.max],
              connect: true,
              range: {
                min: filter.range.min,
                max: filter.range.max,
              },
              step: filter.range.step || 1,
              format: {
                to: (value) => Math.round(value),
                from: (value) => value,
              },
            });

            this.sliderRanges[filter.field] = [
              filter.range.min,
              filter.range.max,
            ];

            sliderElement.noUiSlider.on("update", (values) => {
              this.sliderRanges[filter.field] = values.map(Number);
              this.debouncedUpdateMarkers();
            });
          }
        }
      });
    } catch (error) {
      console.error("Error creating sliders:", error);
    }

    this.mapConfig.filters.forEach((filter) => {
      this.collapsedFilters[filter.field] = true;
    });

    // Stel de filter kleuren in vanuit de configuratie, indien aanwezig
    const root = document.documentElement;
    if (this.mapConfig.filterColors) {
      Object.entries(this.mapConfig.filterColors).forEach(([key, color]) => {
        root.style.setProperty(`--${key}`, color);
      });
    }

    document.addEventListener("fullscreenchange", this.fullscreenChangeHandler);
  },
  computed: {
    /**
     * Kolommen van het tekstalternatief, afgeleid uit dezelfde markertemplate
     * als de kaartpopup. Zo toont de lijst wat de popup toont, zonder een
     * tweede configuratie die kan gaan afwijken.
     */
    lijstKolommen() {
      const velden = this.mapConfig?.markerTemplate?.fields || [];
      const meeteldend = ["header", "subheader", "text"];
      // Markertemplate-velden hebben lang niet altijd een defaultText, maar de
      // filters in dezelfde configuratie dragen wél een leesbaar label voor
      // dezelfde velden. Anders wordt "status_project" de kolomkop.
      const filterLabels = {};
      for (const f of this.mapConfig?.filters || []) {
        if (f.field && f.label) filterLabels[f.field] = f.label;
      }
      const leesbaar = (sleutel) => {
        const tekst = String(sleutel).replace(/[_-]+/g, " ").trim();
        return tekst.charAt(0).toUpperCase() + tekst.slice(1);
      };
      return velden
        .filter((f) => f.key && meeteldend.includes(f.type))
        .map((f) => ({
          key: f.key,
          label: f.defaultText || filterLabels[f.key] || leesbaar(f.key),
          veld: f,
        }));
    },
  },
  methods: {
    /**
     * De skip-link: lijst uitklappen én de focus erin zetten. Een <section> is
     * niet focusbaar, dus zonder tabindex="-1" zet de browser alleen het
     * startpunt voor de volgende Tab — dan sprong je niet, maar moest je nog
     * twee keer verder.
     */
    gaNaarLijst() {
      this.lijstOpen = true;
      this.$nextTick(() => {
        const sectie = this.$refs.projectenlijst;
        if (sectie) {
          sectie.focus();
          sectie.scrollIntoView({ block: "nearest" });
        }
      });
    },

    /**
     * Lijst sluiten en de focus terugzetten op de knop die hem opent. Zonder dat
     * valt de focus naar body en begint een toetsenbordgebruiker weer vooraan.
     */
    sluitLijst() {
      this.lijstOpen = false;
      this.$nextTick(() => {
        const knop = this.$el?.querySelector?.(".lijst-knop");
        if (knop) knop.focus();
      });
    },

    /** Waarde van één kolom voor de lijst, met dezelfde weergaveregels als de popup. */
    lijstWaarde(item, kolom) {
      if (!item || !kolom) return "";
      const waarde = item[kolom.key];
      if (waarde === undefined || waarde === null || waarde === "") return "—";
      if (Array.isArray(waarde)) return waarde.join(", ");
      const prefix = kolom.veld?.prefix || "";
      const suffix = kolom.veld?.suffix || "";
      return `${prefix}${waarde}${suffix}`;
    },

    /**
     * Detailpaneel openen vanuit de lijst. De focus moet terug naar de knop
     * waarmee het paneel geopend is; markers worden bij elke filterwijziging
     * opnieuw opgebouwd, dus een bewaarde marker-node is geen betrouwbaar
     * focusdoel maar deze knop wel.
     */
    toonDetailsUitLijst(item, evt) {
      this.$refs.markerDisplay?.show?.(item, this.mapConfig, evt?.currentTarget || null);
    },

    /**
     * Toegankelijke naam van een marker: kop en subkop uit de markertemplate,
     * dus dezelfde tekst die de gebruiker in de popup ziet.
     */
    markerNaam(item) {
      const delen = this.lijstKolommen
        .slice(0, 2)
        .map((k) => this.lijstWaarde(item, k))
        .filter((v) => v && v !== "—");
      const naam = delen.length ? delen.join(", ") : "Project op de kaart";
      // Bij een gestapelde locatie staat het groepsaantal als badge op de
      // marker. Dat is puur visuele informatie; door het in de naam op te nemen
      // hoort een schermlezergebruiker het ook, en komt de zichtbare tekst weer
      // overeen met de toegankelijke naam.
      if (item._spreadCount > 1) {
        return `${naam} — ${item._spreadCount} projecten op deze locatie`;
      }
      return naam;
    },

    async initMap() {
      try {
        const { Map } = await google.maps.importLibrary("maps");
        const { AdvancedMarkerElement } = await google.maps.importLibrary(
          "marker"
        );
        const center = {
          lat: this.mapConfig.center.lat,
          lng: this.mapConfig.center.lng,
        };
        const isMobile = window.innerWidth <= 768;

        const mapInstance = new Map(document.getElementById("map"), {
          zoom: isMobile
            ? this.mapConfig.zoom?.mobile ?? 9
            : this.mapConfig.zoom?.desktop ?? 11,
          center,
          mapId: "4504f8b37365c3d0",
        });
        // Zorg ervoor dat de map geen reactieve object is (zonder Proxy object),
        // zodat deze gebruikt kan blijven worden in de updateMarkers functie.
        this.map = markRaw(mapInstance);

        // Hier stond een tweede SidebarMarkerDisplay, via createApp gemount in
        // een Maps-control. Die werd nooit aangeroepen — het detailpaneel loopt
        // via de instantie in de template (ref="markerDisplay"). Voor een
        // schermlezer verscheen dat lege paneel wél als tweede regio.

        if (this.properties.length > 0) {
          const clusteringEnabled = this.mapConfig.clustering?.enabled;

          // Bepaal display-posities: waaier samenvallende markers uit (capped jitter).
          // De echte coördinaat blijft in item.position.
          const overflowSignposts = this.computeSpread();

          for (const item of this.properties) {
            // Overflow-leden (boven de max) worden niet los getoond;
            // ze zijn bereikbaar via de "+X meer"-signpost.
            if (item._displayPosition === null) continue;

            const iconType = item.type;
            const cssVar = getComputedStyle(document.documentElement).getPropertyValue(`--${iconType}-color`).trim();
            const color = (item.color || cssVar).trim();

            // Bij clustering: maak markers zonder map, clusterer beheert ze
            // gmpClickable maakt de marker focusbaar en met Enter/Spatie te
            // bedienen; `title` levert de toegankelijke naam. `description` is
            // optioneel en is proza, dus die is als naam onbruikbaar — vandaar
            // een naam uit dezelfde velden als de popupkop.
            const marker = new AdvancedMarkerElement({
              map: clusteringEnabled ? null : this.map,
              content: this.buildContent({ ...item, type: iconType }),
              position: item._displayPosition || item.position,
              title: this.markerNaam(item),
              gmpClickable: true,
            });

            // Stel de initiële achtergrondkleur in
            // Gebruik removeProperty als er geen kleur is zodat de CSS fallback (#cccccc) wél ingaat
            if (color) {
              marker.content.style.setProperty("--marker-bg-color", color);
            } else {
              marker.content.style.removeProperty("--marker-bg-color");
            }

            // Sla de item op in onze Map en niet op de marker zelf omdat dat
            // ervoor zorgt dat deze niet meer getoond wordt op de map.
            this.markerItems.set(marker, item);

            marker.addListener("click", () => {
              this.toggleHighlight(marker, item);
            });

            this.markers.push(marker);

            // Spider-lijntje: verbind een uitgewaaierde marker met het echte
            // groepscentrum, zodat zichtbaar is dat ze bij één locatie horen.
            if (item._isSpread && item._spreadCenter) {
              this.spiderLegs.set(marker, this.buildSpiderLeg(item._spreadCenter, item._displayPosition));
            }
          }

          // Plaats overflow-signposts ("+X meer") voor groepen boven de max.
          // Klik opent de sidebar met de volledige lijst — niets verdwijnt geruisloos.
          for (const sp of overflowSignposts) {
            const overflowMarker = new AdvancedMarkerElement({
              map: clusteringEnabled ? null : this.map,
              content: this.buildOverflowContent(sp.hidden),
              position: sp.center,
              title: `${sp.items.length} projecten op deze locatie`,
            });
            overflowMarker.addListener("click", () => this.showOverflowList(sp.items));
            this.markers.push(overflowMarker);
          }

          // Initialiseer MarkerClusterer als clustering is ingeschakeld
          if (clusteringEnabled) {
            const maxZoom = this.mapConfig.clustering?.maxZoom ?? 15;
            const clusterColors = this.mapConfig.clustering?.colors || {
              small: "#3388ff",
              large: "#ff6b6b",
              border: "#ffffff",
              style: "solid",
            };
            const borderColor = clusterColors.border || "#ffffff";
            const clusterStyle = clusterColors.style || "solid";
            // Font size als relatieve schaal (default 100% = optimale grootte)
            const fontSizeScale = this.mapConfig.clustering?.fontSize || 100;
            
            // Custom renderer voor cluster kleuren
            const customRenderer = {
              render: ({ count, position }, stats) => {
                // Bepaal kleur op basis van gemiddelde
                const color = count > stats.clusters.markers.mean 
                  ? clusterColors.large 
                  : clusterColors.small;
                
                // Bereken grootte op basis van aantal markers
                const size = Math.min(40 + Math.sqrt(count) * 4, 80);
                
                // Bereken font size (100% = optimale basisgrootte, schaalbaar)
                const fontSize = Math.round(size * 0.4 * fontSizeScale / 100);
                
                let svg;
                if (clusterStyle === "glow") {
                  // Glow stijl: drie concentrische cirkels met afnemende opacity (25%, 50%, 100%)
                  const outerRadius = size / 2 - 1;
                  const middleRadius = size * 0.38;
                  const innerRadius = size * 0.28;
                  svg = `
                    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="${size / 2}" cy="${size / 2}" r="${outerRadius}" fill="${color}" opacity="0.25"/>
                      <circle cx="${size / 2}" cy="${size / 2}" r="${middleRadius}" fill="${color}" opacity="0.5"/>
                      <circle cx="${size / 2}" cy="${size / 2}" r="${innerRadius}" fill="${color}"/>
                      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="${fontSize}px" font-weight="bold">${count}</text>
                    </svg>
                  `;
                } else {
                  // Solid stijl: strakke rand met configureerbare kleur
                  svg = `
                    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="${borderColor}" stroke-width="2"/>
                      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="${fontSize}px" font-weight="bold">${count}</text>
                    </svg>
                  `;
                }
                
                const div = document.createElement("div");
                div.innerHTML = svg;
                div.style.cursor = "pointer";
                
                return new google.maps.marker.AdvancedMarkerElement({
                  position,
                  content: div,
                  zIndex: count,
                });
              },
            };
            
            this.markerClusterer = new MarkerClusterer({
              map: this.map,
              markers: this.markers,
              algorithm: new SuperClusterAlgorithm({ maxZoom }),
              renderer: customRenderer,
            });
          }

          // Pas filters (incl. publicatie-filter) toe op de initieel geladen markers
          this.updateMarkers();
        }
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    },
    toggleHighlight(markerView, item) {
      // Sluit eerst alle andere open markers
      this.markers.forEach(marker => {
        if (marker !== markerView && marker.content.classList.contains('highlight')) {
          this.closeMarker(marker);
        }
      });

      if (this.displayMode === 'sidebar') {
        // Sidebar mode met mapConfig
        this.$refs.markerDisplay?.show(item, this.mapConfig);
      } else {
        // Popup mode
        if (markerView.content.classList.contains('highlight')) {
          this.closeMarker(markerView);
          this.$refs.popupDisplay?.hide();
        } else {
          markerView.content.classList.add('highlight');
          markerView.zIndex = 1;
          const iconElement = markerView.content.querySelector('.icon');
          const color = item.color || getComputedStyle(document.documentElement).getPropertyValue(
            `--${item.type}-color`
          );
          iconElement.style.color = color;
          markerView.content.style.setProperty('--marker-bg-color', '');
          
          this.$refs.popupDisplay?.show(item);
        }
      }
    },
    closeMarker(marker) {
      const iconElement = marker.content.querySelector('.icon');
      const markerElement = marker.content;
      const markerItem = this.markerItems.get(marker);
      const color = (markerItem.color || getComputedStyle(document.documentElement).getPropertyValue(
        `--${markerItem.type}-color`
      )).trim();
      
      markerElement.classList.remove('highlight');
      marker.zIndex = null;
      iconElement.style.color = '';
      if (color) {
        markerElement.style.setProperty('--marker-bg-color', color);
      } else {
        markerElement.style.removeProperty('--marker-bg-color');
      }
    },
    buildContent(item) {
      const content = document.createElement("div");
      content.classList.add("marker");

      const markerContent = document.createElement("div");
      markerContent.classList.add("icon");
      markerContent.innerHTML = `
        <i aria-hidden="true" class="fa ${this.getIconClass(item.type)}" title="${item.type}"></i>
        <span class="fa-sr-only">${item.type}</span>
      `;
      content.appendChild(markerContent);

      // Badge met het groepsaantal bij gestapelde (gespreide) markers,
      // zodat je in één oogopslag ziet hoeveel er op deze locatie liggen.
      if (item._spreadCount > 1) {
        const badge = document.createElement("div");
        badge.classList.add("count-badge");
        badge.textContent = String(item._spreadCount);
        content.appendChild(badge);
      }

      const detailsContainer = document.createElement("div");
      detailsContainer.classList.add("details");
      
      const mapConfig = this.mapConfig;
      
      const vueApp = createApp({
        setup() {
          provide('mapConfig', ref(mapConfig));
          return {};
        },
        render: () => h(MarkerTemplate, { item })
      });
      
      // Error handler om crashes in MarkerTemplate zichtbaar te maken
      vueApp.config.errorHandler = (err, instance, info) => {
        console.error('[MarkerTemplate Error]', err);
        console.error('[MarkerTemplate Error] Item data:', JSON.stringify(item, null, 2));
        console.error('[MarkerTemplate Error] Component info:', info);
      };
      
      vueApp.mount(detailsContainer);
      content.appendChild(detailsContainer);

      return content;
    },
    getIconClass(icon) {
      return icon?.startsWith('fa-') ? icon : `fa-${icon}`;
    },
    getSpreadConfig() {
      // Defaults; overschrijfbaar via mapConfig.spread.
      // radiusPerPointMeters: de straal groeit mee met het aantal punten op de ring
      // (straal = radiusPerPointMeters × aantal), zodat een grotere stapel een grotere burst geeft.
      // startAngleDeg: hoek waar de ring begint (-90° = eerste twee punten links/rechts náást elkaar).
      return {
        max: 8, toleranceDeg: 0.00003, radiusPerPointMeters: 1.5, startAngleDeg: -90,
        legColor: "#455a64", legOpacity: 0.55, legWeight: 1.5, // spider-lijntje naar het centrum
        ...(this.mapConfig.spread || {}),
      };
    },
    // Groepeer items op (bijna) gelijke coördinaat. Retourneert groepen met
    // hun leden en het gemiddelde middelpunt. Gedeeld door jitter en detectOverlaps.
    groupByPosition(items, toleranceDeg) {
      const map = new Map();
      items.forEach((item) => {
        const lat = item.position?.lat;
        const lng = item.position?.lng;
        if (typeof lat !== "number" || typeof lng !== "number" || isNaN(lat) || isNaN(lng)) return;
        const key = `${Math.round(lat / toleranceDeg)},${Math.round(lng / toleranceDeg)}`;
        if (!map.has(key)) map.set(key, { items: [], sumLat: 0, sumLng: 0 });
        const g = map.get(key);
        g.items.push(item);
        g.sumLat += lat;
        g.sumLng += lng;
      });
      return Array.from(map.values()).map((g) => ({
        items: g.items,
        center: { lat: g.sumLat / g.items.length, lng: g.sumLng / g.items.length },
      }));
    },
    // Bereken een positie op een ring rond het middelpunt (voor de spread).
    // startAngleRad verschuift de startpositie (bv. -π/2 = eerste punt links).
    ringPosition(center, index, count, radiusMeters, startAngleRad = 0) {
      const angle = startAngleRad + (2 * Math.PI * index) / count;
      const metersPerDegLat = 111320;
      const dLat = (radiusMeters / metersPerDegLat) * Math.cos(angle);
      const dLng = (radiusMeters / (metersPerDegLat * Math.cos((center.lat * Math.PI) / 180))) * Math.sin(angle);
      return { lat: center.lat + dLat, lng: center.lng + dLng };
    },
    // Zet per item een _displayPosition (echte coördinaat blijft in item.position):
    //  - unieke locatie -> eigen positie
    //  - groep 2..max   -> uitgewaaierd op een ring
    //  - boven de max   -> null (niet los getoond; bereikbaar via overflow-lijst)
    // Retourneert de overflow-signposts die als extra marker geplaatst moeten worden.
    computeSpread() {
      const cfg = this.getSpreadConfig();
      const startAngleRad = ((cfg.startAngleDeg || 0) * Math.PI) / 180;
      const overflow = [];
      this.groupByPosition(this.properties, cfg.toleranceDeg).forEach((group) => {
        const items = group.items;
        const k = items.length;
        if (k === 1) {
          items[0]._displayPosition = items[0].position;
          items[0]._isSpread = false;
          items[0]._spreadCount = 1;
          return;
        }
        const shown = Math.min(k, cfg.max);
        // Hoeken gelijkmatig over 360° vanaf startAngleDeg (-90° = koppeltje links/rechts),
        // straal groeit mee met het aantal punten op de ring → een duidelijke, gelijkmatige
        // "burst" die stapeling zichtbaar maakt.
        const radius = cfg.radiusPerPointMeters * shown;
        for (let i = 0; i < k; i++) {
          const onRing = i < shown;
          items[i]._displayPosition = onRing
            ? this.ringPosition(group.center, i, shown, radius, startAngleRad)
            : null;
          items[i]._isSpread = onRing; // gespreid → half-transparant tonen
          items[i]._spreadCount = k;   // groepsaantal → badge-getal op het icoon
          items[i]._spreadCenter = onRing ? group.center : null; // ankerpunt voor het spider-lijntje
        }
        if (k > cfg.max) overflow.push({ center: group.center, items, hidden: k - shown });
      });
      return overflow;
    },
    // DOM voor een overflow-signpost ("+X meer") op een samenvallende groep.
    buildOverflowContent(hiddenCount) {
      const div = document.createElement("div");
      div.textContent = `+${hiddenCount}`;
      div.title = `Nog ${hiddenCount} op deze locatie — klik voor de lijst`;
      div.style.cssText =
        "display:flex;align-items:center;justify-content:center;min-width:34px;height:34px;padding:0 8px;" +
        "border-radius:17px;background:#455a64;color:#fff;font-weight:bold;font-size:13px;border:2px solid #fff;" +
        "box-shadow:0 1px 4px rgba(0,0,0,.4);cursor:pointer;white-space:nowrap;";
      return div;
    },
    // Subtiel "spider"-lijntje van een uitgewaaierde marker naar het groepscentrum.
    // clickable:false zodat het klikken op markers/kaart niet onderschept.
    buildSpiderLeg(center, pos) {
      const cfg = this.getSpreadConfig();
      return markRaw(new google.maps.Polyline({
        path: [center, pos],
        map: this.map,
        strokeColor: cfg.legColor,
        strokeOpacity: cfg.legOpacity,
        strokeWeight: cfg.legWeight,
        clickable: false,
        zIndex: 0,
      }));
    },
    // Toon alle records van een samenvallende groep in de sidebar (lijst-modus).
    showOverflowList(items) {
      this.$refs.markerDisplay?.showList?.(items, this.mapConfig);
    },
    updateMarkers() {
      try {
        const visibleMarkers = [];
        const zichtbareItems = [];

        const pubFilter = this.mapConfig.publicationFilter;
        const pubFilterActive = !!(pubFilter && pubFilter.enabled && pubFilter.property);
        const pubFilterMode = pubFilter && pubFilter.mode === 'exclude' ? 'exclude' : 'include';
        let pubFilterHiddenCount = 0;
        let searchHiddenCount = 0;
        const fieldFilterCounts = {};

        this.markers.forEach((marker) => {
          let visible = true;
          const item = this.markerItems.get(marker);

          // Overflow-signposts hebben geen record-item → altijd tonen (filters n.v.t.)
          if (!item) {
            visibleMarkers.push(marker);
            if (!this.markerClusterer) marker.setMap(this.map);
            return;
          }

          // Publicatie-filter: include = alleen tonen bij match, exclude = verbergen bij match
          if (pubFilterActive) {
            const recordValue = item[pubFilter.property];
            const target = String(pubFilter.value);
            let matches = false;
            if (Array.isArray(recordValue)) {
              // Checkbox-array: option-namen als strings
              matches = recordValue.some(v => String(v) === target);
            } else if (recordValue && typeof recordValue === 'object') {
              // Legacy checkbox-object: { optionName: true/false }
              matches = recordValue[target] === true || recordValue[target] === 'true';
            } else {
              matches = String(recordValue) === target;
            }
            if (pubFilterMode === 'exclude') {
              if (matches) { visible = false; pubFilterHiddenCount++; }
            } else {
              if (!matches) { visible = false; pubFilterHiddenCount++; }
            }
          }

          // Filter op basis van de searchQuery
          if (visible && this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            
            // Zoek in alle normale velden
            let matchesQuery = Object.entries(item).some(([key, value]) => {
              // Skip position en features objecten voor deze check
              if (key === 'position' || key === 'features') return false;
              
              // Controleer of de waarde de zoekopdracht bevat
              return String(value).toLowerCase().includes(query);
            });
            
            // Als er nog geen match is, zoek alleen in feature.value velden
            if (!matchesQuery && item.features && item.features.items) {
              matchesQuery = item.features.items.some(feature => 
                feature.value && String(feature.value).toLowerCase().includes(query)
              );
            }
            
            if (!matchesQuery) {
              visible = false;
              searchHiddenCount++;
            }
          }

          // Waardes vergelijken we case-insensitief en zonder randspaties,
          // zodat datavervuiling ("woning" vs "Woning") geen markers verbergt
          const norm = (v) => String(v ?? "").toLowerCase().trim();

          this.mapConfig.filters.forEach((filter) => {
            const wasVisibleBefore = visible;
            if (filter.type === "checkbox") {
              const selectedOptions = this.selectedOptions[filter.field];
              const markerValue = item[filter.field];

              // Lege of ontbrekende selectie = geen restrictie (filter inactief)
              if (!selectedOptions || selectedOptions.length === 0) {
                return;
              }

              if (Array.isArray(markerValue)) {
                const markerValues = markerValue.map(norm);
                if (!selectedOptions.some(option => markerValues.includes(norm(option)))) {
                  visible = false;
                }
              } else {
                if (!selectedOptions.some(option => norm(option) === norm(markerValue))) {
                  visible = false;
                }
              }
            } else if (filter.type === "range") {
              const value = item[filter.field];
              const range = this.sliderRanges[filter.field];
              if (value < range[0] || value > range[1]) {
                visible = false;
              }
            } else if (filter.type === "select" || filter.type === "radio") {
              const selectedOption = this.selectedOptions[filter.field];
              // Lege selectie ('niet geselecteerd') = filter inactief, alle records doorlaten
              if (selectedOption === '' || selectedOption === null || selectedOption === undefined) {
                return;
              }
              if (norm(selectedOption) !== norm(item[filter.field])) {
                visible = false;
              }
            }
            if (wasVisibleBefore && !visible) {
              fieldFilterCounts[filter.field] = (fieldFilterCounts[filter.field] || 0) + 1;
            }
          });

          if (visible) {
            visibleMarkers.push(marker);
            zichtbareItems.push(item);
          }

          // Als clustering actief is, laat de clusterer de markers beheren
          if (!this.markerClusterer) {
            marker.setMap(visible ? this.map : null);
          }

          // Spider-lijntje volgt de zichtbaarheid van zijn marker (los van de clusterer)
          const leg = this.spiderLegs.get(marker);
          if (leg) leg.setMap(visible ? this.map : null);
        });
        
        // Update de clusterer met alleen zichtbare markers
        if (this.markerClusterer) {
          this.markerClusterer.clearMarkers();
          this.markerClusterer.addMarkers(visibleMarkers);
        }

        // Bouw filterStats voor diagnostics
        const fieldFilterStats = (this.mapConfig.filters || [])
          .filter((f) => fieldFilterCounts[f.field] > 0)
          .map((f) => ({
            field: f.field,
            label: f.label || f.field,
            type: f.type,
            hidden: fieldFilterCounts[f.field],
          }));

        // Houd het actuele aantal zichtbare markers bij voor diagnostics
        this.visibleMarkerCount = visibleMarkers.length;
        this.zichtbareItems = zichtbareItems;
        this.pubFilterHiddenCount = pubFilterHiddenCount;
        this.pubFilterActiveSnapshot = pubFilterActive
          ? { mode: pubFilterMode, property: pubFilter.property, value: pubFilter.value }
          : null;
        this.searchHiddenCount = searchHiddenCount;
        this.fieldFilterStats = fieldFilterStats;
        if (this.diagnosticsPanelData) {
          this.diagnosticsPanelData.visibleRecords = visibleMarkers.length;
          this.diagnosticsPanelData.pubFilterHidden = pubFilterHiddenCount;
          this.diagnosticsPanelData.pubFilter = this.pubFilterActiveSnapshot;
          this.diagnosticsPanelData.searchHidden = searchHiddenCount;
          this.diagnosticsPanelData.searchQuery = this.searchQuery || '';
          this.diagnosticsPanelData.fieldFilters = fieldFilterStats;
        }
      } catch (error) {
        console.error("Error updating markers:", error);
      }
    },
    resetFilters() {
      this.mapConfig.filters.forEach((filter) => {
        if (filter.type === "checkbox") {
          // Gebruik defaultSelected als het bestaat, anders alle opties als includeAllWhenEmpty true is
          this.selectedOptions[filter.field] = filter.defaultSelected 
            ? filter.options.map(opt => opt.value)
            : (filter.includeAllWhenEmpty ? filter.options.map(opt => opt.value) : []);
        } else if (filter.type === "select" || filter.type === "radio") {
          // Reset naar 'niet geselecteerd' (lege string), tenzij admin een defaultValue ingesteld heeft
          this.selectedOptions[filter.field] = filter.defaultValue ?? '';
        } else if (filter.type === "range") {
          const sliderElement = this.$refs[filter.field + "Slider"]?.[0];
          if (sliderElement?.noUiSlider) {
            sliderElement.noUiSlider.set([filter.range.min, filter.range.max]);
          }
        }
        this.collapsedFilters[filter.field] = true;
      });

      this.searchQuery = "";
      this.updateMarkers();
    },
    updateMarkerColors() {
      const root = document.documentElement;
      if (this.mapConfig.colors) {
        Object.entries(this.mapConfig.colors).forEach(([type, color]) => {
          root.style.setProperty(`--${type}-color`, color);
        });
      }
    },
    toggleCollapse(field) {
      this.collapsedFilters[field] = !this.collapsedFilters[field];
    },
    isCollapsed(field) {
      return this.collapsedFilters[field];
    },
    fullscreenChangeHandler() {
      const fsElement = document.fullscreenElement;
      this.isFullscreen = !!fsElement;
      const filterSidebar = this.$refs.filterSidebar;
      const markerDisplay = this.$refs.markerDisplay?.$el;
      if (!filterSidebar) return;

      if (fsElement) {
        // In full screen: verplaats filter sidebar én marker sidebar in het full screen element
        fsElement.appendChild(filterSidebar);
        if (markerDisplay) {
          fsElement.appendChild(markerDisplay);
        }
      } else {
        // Niet full screen: zet beide terug in de originele container (.map-container)
        const defaultContainer = document.querySelector(".map-container");
        if (defaultContainer) {
          // Plaats de filter sidebar vooraan (zorgt dat hij zichtbaar blijft, bijvoorbeeld links)
          defaultContainer.insertBefore(filterSidebar, defaultContainer.firstChild);
          if (markerDisplay) {
            defaultContainer.appendChild(markerDisplay);
          }
        }
      }
    },
    async toggleFullscreen() {
      const container = document.querySelector(".map-container");
      if (!container) return;
      try {
        if (!document.fullscreenElement) {
          await container.requestFullscreen();
        } else {
          await document.exitFullscreen();
        }
      } catch (error) {
        console.error("Fullscreen toggle failed:", error);
      }
    },

    // ========== Admin Diagnostics Communicatie ==========
    
    setupParentCommunication() {
      window.addEventListener('message', this.handleParentMessage);
    },

    handleParentMessage(event) {
      // Valideer origin
      const allowedOrigins = process.env.VUE_APP_ADMIN_ORIGINS 
        ? process.env.VUE_APP_ADMIN_ORIGINS.split(',') 
        : [];
      
      // Accepteer localhost origins altijd (voor development)
      const isLocalhost = event.origin.includes('localhost') || event.origin.includes('127.0.0.1');
      
      // Blokkeer alleen als: niet localhost EN allowedOrigins is geconfigureerd EN origin staat niet in de lijst
      if (!isLocalhost && allowedOrigins.length > 0 && !allowedOrigins.includes(event.origin)) {
        return;
      }
      
      // Sla de parent origin op voor responses - maar alleen als het van een ANDERE origin komt
      // (niet van dezelfde origin als de iframe zelf, bijv. recaptcha)
      if (event.origin !== window.location.origin) {
        this.parentOrigin = event.origin;
      }

      const { type } = event.data || {};
      
      switch (type) {
        case 'enableDiagnostics':
        case 'showDiagnostics':
          if (event.data.adminOrigin) {
            this.parentOrigin = event.data.adminOrigin;
          }
          this.showDiagnosticsPanel = true;
          this.diagnosticsPanelData = this.collectDiagnostics();
          break;
        case 'hideDiagnostics':
          this.showDiagnosticsPanel = false;
          break;
        case 'requestDiagnostics':
          this.sendDiagnosticsToParent();
          break;
        default:
          break;
      }
    },

    sendToParent(message) {
      if (!this.isInIframe) {
        return;
      }
      
      try {
        window.parent.postMessage(message, '*');
      } catch (error) {
        console.error('MapView: Kon bericht niet naar parent sturen:', error);
      }
    },


    collectDiagnostics() {
      // Haal formId uit eerste property (indien beschikbaar)
      const formId = this.properties[0]?.formId || this.mapConfig?.dataSetId;
      
      // Start met backend diagnostics (errors van ongeldige records)
      const diagnostics = {
        totalRecords: this.diagnosticsData?.totalRecords || this.properties.length,
        validRecords: this.diagnosticsData?.validRecords || this.properties.length,
        visibleRecords: this.visibleMarkerCount || this.properties.length,
        pubFilterHidden: this.pubFilterHiddenCount,
        pubFilter: this.pubFilterActiveSnapshot,
        searchHidden: this.searchHiddenCount,
        searchQuery: this.searchQuery || '',
        fieldFilters: this.fieldFilterStats,
        errors: this.diagnosticsData?.errors || [],
        warnings: this.diagnosticsData?.warnings || [],
        overlaps: this.detectOverlaps(),
        formId: formId,
        adminOrigin: this.parentOrigin || ''
      };

      return diagnostics;
    },

    detectOverlaps() {
      const TOLERANCE = 0.0001; // ~11 meter op de evenaar
      return this.groupByPosition(this.properties, TOLERANCE)
        .filter((g) => g.items.length > 1)
        .map((g) => ({ position: g.center, count: g.items.length, ids: g.items.map((i) => i.id) }))
        .sort((a, b) => b.count - a.count);
    },

    sendDiagnosticsToParent() {
      const diagnostics = this.collectDiagnostics();
      this.sendToParent({
        type: 'diagnostics',
        data: diagnostics
      });
    },
  },

  beforeUnmount() {
    document.removeEventListener("fullscreenchange", this.fullscreenChangeHandler);
    // Cleanup postMessage listener
    if (this.isInIframe) {
      window.removeEventListener('message', this.handleParentMessage);
    }
  },
};
</script>

<style>
.map-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  margin: 0;
}

/* Skip-link: onzichtbaar tot hij focus krijgt. Niet met display:none
   verbergen, want dan is hij ook niet met Tab te bereiken.

   position: fixed en een z-index bóven de zijbalk (9999999): met absolute en
   z-index 2000 kreeg de link wel focus, maar schoof het filterpaneel eroverheen
   en zag je hem niet. Een skip-link die je niet ziet is geen skip-link. */
.skip-link {
  position: fixed;
  left: -9999px;
  top: 0;
  z-index: 10000000;
  background: #123a57;
  color: #fff;
  padding: 0.6rem 1rem;
  text-decoration: none;
  border-radius: 0 0 4px 0;
}
.skip-link:focus {
  left: 0;
  top: 0;
  outline: 3px solid #ffd23f;
  outline-offset: -3px;
}

/* Bedieningshint: verborgen tot de focus in de kaart staat. :focus-within werkt
   ook als de focus in de shadow DOM van een Google-marker zit, dus dit heeft
   geen JavaScript nodig. */
.kaart-hint {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 1rem;
  z-index: 9999999;
  background: #123a57;
  color: #fff;
  padding: 0.5rem 0.9rem;
  border-radius: 4px;
  font-size: 0.85rem;
  box-shadow: 0 2px 10px rgba(16, 32, 52, 0.3);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}
#map:focus-within + .kaart-hint {
  opacity: 1;
  visibility: visible;
}
.kaart-hint kbd {
  background: rgba(255, 255, 255, 0.16);
  border-radius: 3px;
  padding: 0 0.3em;
  font-family: inherit;
}
@media (prefers-reduced-motion: no-preference) {
  .kaart-hint { transition: opacity 0.15s ease; }
}

/* Tekstalternatief onderaan de kaart. De kaart vult het hele venster en
   knipt alles wat erbuiten valt, dus de lijst is een paneel en geen blok
   eronder — dat zou nooit zichtbaar worden. */
.projectenlijst {
  position: fixed;
  right: 0;
  left: auto;
  bottom: 0;
  /* Rechtsonder gedokt: het filterpaneel staat links en vast op een zeer hoge
     z-index, en de uitklapknop daarvan hangt linksonder. Vanaf rechts komt de
     lijst geen van beide tegen. */
  width: min(760px, 100vw);
  z-index: 9999998;
  background: #fff;
  border-top: 1px solid #dee2e6;
  border-left: 1px solid #dee2e6;
  box-shadow: 0 -2px 8px rgba(16, 32, 52, 0.12);
  display: flex;
  flex-direction: column;
  max-height: 60vh;
}
/* Compacte knop wanneer de lijst dicht is, in dezelfde stijl als de
   volledig-schermknop rechtsboven. */
.lijst-knop {
  position: fixed;
  right: 10px;
  bottom: 10px;
  z-index: 9999998;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  height: 40px;
  padding: 0 0.85rem;
  border: none;
  border-radius: 4px;
  background: #ffffff;
  color: #333333;
  font-size: 0.875rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.lijst-knop:hover {
  background: #f1f3f5;
}
.lijst-knop-telling {
  background: #123a57;
  color: #fff;
  border-radius: 10px;
  padding: 0 0.45rem;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.lijst-kop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  flex: 0 0 auto;
}
.lijst-inhoud {
  overflow: auto;
  padding: 0 0.75rem 0.75rem;
}

/* Landingsplek voor de skip-link. Wel een zichtbare ring: anders zie je niet
   dat de focus verplaatst is. */
.projectenlijst:focus-visible {
  outline: 3px solid #123a57;
  outline-offset: -3px;
}
.projectenlijst .table {
  font-size: 0.875rem;
}

#map {
  height: 100%;
  width: 100%;
  z-index: 1 !important; /* Forceer een lage z-index voor de map */
}

.map-fullscreen-toggle {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 4px;
  background: #ffffff;
  color: #333333;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background-color 0.15s ease;
}

.map-fullscreen-toggle:hover {
  background: #f5f5f5;
}

.map-fullscreen-toggle:focus {
  outline: 2px solid #4285f4;
  outline-offset: 2px;
}

.marker {
  align-items: center;
  background-color: var(--marker-bg-color, #cccccc);
  border-radius: 50%;
  color: #263238;
  display: flex;
  font-size: 14px;
  gap: 15px;
  height: 30px;
  justify-content: center;
  padding: 4px;
  position: relative;
  transition: all 0.3s ease-out;
  width: 30px;
}

.marker::after {
  border-left: 9px solid transparent;
  border-right: 9px solid transparent;
  border-top: 9px solid var(--marker-bg-color, #cccccc);
  content: "";
  height: 0;
  left: 50%;
  position: absolute;
  bottom: -6px;
  transform: translate(-50%, 0);
  transition: all 0.3s ease-out;
  width: 0;
  z-index: 1;
}

.marker .count-badge {
  align-items: center;
  background: #263238;
  border: 1.5px solid #ffffff;
  border-radius: 9px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  color: #ffffff;
  display: flex;
  font-size: 11px;
  font-weight: 700;
  height: 18px;
  justify-content: center;
  line-height: 1;
  min-width: 18px;
  padding: 0 4px;
  position: absolute;
  right: -7px;
  top: -7px;
  z-index: 2;
}

.marker .icon {
  align-items: center;
  display: flex;
  justify-content: center;
  color: #ffffff;
}

.marker .icon svg {
  height: 20px;
  width: auto;
}

.marker .details {
  display: none;
  flex-direction: column;
  flex: 1;
}

.marker .address {
  color: #666666;
  font-size: 10px;
  margin-bottom: 10px;
  margin-top: 5px;
  max-width: 200px;
  white-space: normal;
  overflow-wrap: break-word;
}

.marker .text {
  font-size: 10px;
  margin-bottom: 10px;
  margin-top: 5px;
  max-width: 200px;
  white-space: normal;
  overflow-wrap: break-word;
}

.marker .link {
  font-size: 10px;
  margin-bottom: 10px;
  margin-top: 5px;
}

.marker .image {
  margin-bottom: 10px;
  margin-top: 5px;
}

.marker .features {
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 5px;
  max-width: 100%;
  margin-top: 5px;
  margin-bottom: 5px;
}

.marker .features > div {
  align-items: center;
  background: #f5f5f5;
  border-radius: 5px;
  border: 1px solid #ccc;
  display: flex;
  font-size: 10px;
  gap: 5px;
  padding: 5px;
  flex-shrink: 0;
}

.marker .tags > div {
  background: #f5f5f5;
  border-radius: 5px;
  border: 1px solid #ccc;
  display: inline-flex; 
  font-size: 10px;
  gap: 5px;
  padding: 5px;
  width: fit-content; 
  margin-right: 5px; 
  margin-bottom: 5px; 
}

.marker .tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 10px; 
}

.marker.highlight {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 10px 10px 5px rgba(0, 0, 0, 0.2);
  height: auto;
  min-height: 80px;
  padding: 8px 15px;
  width: auto;
  max-width: 300px;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
}

.marker.highlight::after {
  border-top: 9px solid #ffffff;
}

.marker.highlight .details {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: visible;
  text-align: left;
}

.marker.highlight .icon {
  display: flex;
  align-items: center;
  padding-top: 0;
  min-height: 100%;
}

.marker.highlight .icon i {
  font-size: 2em;  /* 2x zo groot in uitgeklapte staat */
}

.marker.highlight .price,
.marker.highlight .address,
.marker.highlight .text,
.marker.highlight .link,
.marker.highlight .features {
  text-align: left;
  padding-left: 0;
}

.marker.highlight .features {
  justify-content: flex-start;
}

.sidebar {
  position: fixed;
  top: 0;
  left: -300px;
  width: 300px;
  height: 100vh;
  background-color: var(--filter-bg-color);
  z-index: 9999999 !important;
  transition: left 0.3s ease;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}

.sidebar-open {
  left: 0;
}

.sidebar-toggle {
  position: absolute;
  right: -40px;
  bottom: 40px;
  width: 40px;
  height: 40px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 0 4px 4px 0;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  z-index: 9999999 !important; /* Zelfde als sidebar */
  border: 1px solid var(--filter-btn-border-color, #007bff);
  color: var(--filter-btn-color, #007bff);
}

.sidebar-toggle:hover {
  background-color: var(--filter-btn-hover-bg-color, #0056b3);
  color: #fff;
}

@media (max-width: 768px) {
  .sidebar-toggle {
    bottom: 120px;
  }
}

.form-check-input {
  cursor: pointer;
}

/* Voor de noUiSlider-handles */
.slider-container .noUi-handle {
  cursor: pointer;
}

.slider-container .noUi-target {
  margin: 0 10px;  /* Voeg horizontale marge toe */
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.1);
  height: 8px !important;
}

.slider-container .noUi-connect {
  background-color: #007bff;
  height: 8px !important;
}

.slider-container .noUi-handle {
  border: 2px solid #007bff;
  border-radius: 50%;
  width: 16px !important;
  height: 16px !important;
  box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.1);
  background-color: #ffffff !important;
}

.slider-container .noUi-handle::before,
.slider-container .noUi-handle::after {
  content: none !important;
}

.slider-container .noUi-horizontal .noUi-handle {
  top: -5px !important;
  right: -8px !important;
  width: 16px !important;
  height: 16px !important;
}

/* Zorg dat beide handles dezelfde positie hebben */
.slider-container .noUi-horizontal .noUi-handle-lower,
.slider-container .noUi-horizontal .noUi-handle-upper {
  right: -8px !important;
}

.slider-container .noUi-tooltip {
  background-color: #007bff;
  color: #ffffff;
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.details-container {
  max-width: 300px; /* Stel een maximale breedte in voor de inhoud */
  padding: 10px; /* Voeg padding toe om ruimte te geven aan de rounded-box */
}

.details-container .details {
  display: block;
  overflow-x: hidden; /* Voorkom horizontale scrollbalken */
}

.details-container .col-12 {
  text-align: left; /* Zorg ervoor dat de tekst links is uitgelijnd */
  white-space: nowrap; /* Voorkom dat de tekst op een nieuwe regel wordt gezet */
  overflow: hidden; /* Verberg eventuele overlopende tekst */
  text-overflow: ellipsis; /* Voeg ellipsen toe aan overlopende tekst */
}

.details-container .text-muted-small {
  color: #6c757d;
  font-size: 0.7em;
}

.details-container .rounded-box {
  background-color: #f0f0f0;
  padding: 2px;
  border-radius: 4px;
}

.details-container {
  max-width: 300px; /* Stel een maximale breedte in voor de inhoud */
  padding: 10px; /* Voeg padding toe om ruimte te geven aan de rounded-box */
  overflow-x: hidden; /* Voorkom horizontale scrollbalken */
}

.details-container .details {
  display: block;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.details-container .text-muted-small {
  color: #6c757d;
  font-size: 0.7em;
}

.details-container .rounded-box {
  background-color: #f0f0f0;
  padding: 2px;
  border-radius: 4px;
  overflow: visible;
}

.btn-link {
  text-decoration: none;
}

/* De filtertitel zat eerst in een <label> met de normale tekstkleur. Nu hij in
   een knop staat, erft hij het linkblauw van Bootstrap (#0d6efd), en dat haalt
   op de standaard paneelachtergrond (#dddddd) maar 3,31:1 — onder de eis van
   4,5:1. Een donkere tekstkleur past ook beter bij wat het is: een kopje. */
.filter-toggle {
  color: var(--filter-title-color, #212529);
  text-align: left;
}

.filter-toggle:hover,
.filter-toggle:focus {
  color: var(--filter-title-color, #212529);
  text-decoration: underline;
}

.btn-link .fas {
  font-size: 1rem;
  margin-right: 0.5rem;
}

.d-flex.align-items-center {
  justify-content: flex-start;
}

.filter-panel {
  position: relative;
  z-index: 9999999 !important;
  background-color: var(--filter-bg-color);
}

.filter-panel .btn-outline-secondary {
  border-color: var(--filter-btn-border-color, #007bff);
  color: var(--filter-btn-color, #007bff);
}

.filter-panel .btn-outline-secondary:hover {
  background-color: var(--filter-btn-color, #0056b3) !important;
  border-color: var(--filter-btn-color, #0056b3) !important;
  color: #fff !important;
}

/* Checkbox styling */
.form-check-input {
  accent-color: var(
    --filter-btn-color,
    #007bff
  ); /* Gebruik de configuratie of standaard blauw */
}

/* noUiSlider styling */
.slider-container .noUi-connect {
  background-color: var(--filter-btn-color, #007bff);
}

.slider-container .noUi-handle {
  border: 2px solid var(--filter-btn-color, #007bff);
}

/* Caret styling */
.btn-link .fas {
  color: var(--filter-btn-color, #007bff);
}

/* Specifieke styling voor checkboxes binnen de filter-panel */
.filter-panel .form-check-input:checked {
  background-color: var(--filter-btn-color, #007bff);
  border-color: var(--filter-btn-color, #007bff);
}

/* Zorg ervoor dat de cursor een handje wordt bij hover over de label */
.filter-panel .form-label {
  cursor: pointer;
}

.marker.highlight .price,
.marker.highlight .address {
  font-weight: bold;
}

.marker .header,
.marker .subheader {
  max-width: 200px;
  white-space: normal;
  overflow-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
}

.marker .header {
  font-weight: bold;
  font-size: 14px;
  overflow-wrap: break-word;
  margin-bottom: 5px;
}

.marker .subheader {
  font-size: 0.8em;
  font-weight: bold;
  margin-bottom: 5px;
  margin-top: 0px;
  max-width: 200px;
  white-space: normal;
  overflow-wrap: break-word;
}

.marker .text {
  font-size: 10px;
  margin-bottom: 5px;
  margin-top: 5px;
  max-width: 200px;
  white-space: normal;
  overflow-wrap: break-word;
}

.marker .text strong {
  font-weight: bold;
  margin-right: 4px;
}

/* Checkbox labels links uitlijnen */
.form-check {
  text-align: left;
  padding-left: 2rem;  /* Ruimte voor de checkbox */
}

.form-check-label {
  font-size: 0.9rem;
  text-align: left;
  margin-left: 0.5rem;
}

/* Voor de filter titels */
.form-label {
  font-size: 0.9rem;
  font-weight: bold;
  text-align: left;
  width: 100%;
}

/* Voor de filter headers */
.filter-panel h5 {
  text-align: left;
}

/* Voor select dropdowns */
.form-select {
  font-size: 0.9rem;
  text-align: left;
}

.slider-container .form-label {
  font-size: 0.9rem;
  font-weight: normal;  /* Verwijder bold voor slider labels */
}

/* Voor text inputs */
.form-control {
  font-size: 0.9rem;
}

.filter-container {
  background-color: var(--filter-bg-color) !important;
}
</style>
